import { useState } from 'react'
import { z } from 'zod'
import { useForm } from 'react-hook-form'
import { zodResolver } from '@hookform/resolvers/zod'
import { useClinics } from '@/hooks/useClinics'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { Card, CardContent, CardDescription, CardFooter, CardHeader, CardTitle } from '@/components/ui/card'
import { toast } from '@/hooks/use-toast'

const clinicSchema = z.object({
  name: z.string().min(2, 'Name must be at least 2 characters'),
  address: z.string().min(5, 'Address must be at least 5 characters'),
  phone: z.string().min(10, 'Phone must be at least 10 characters'),
  rfc: z.string().min(12, 'RFC must be 12-13 characters').max(13, 'RFC must be 12-13 characters'),
})

type ClinicFormValues = z.infer<typeof clinicSchema>

interface ClinicCreationFormProps {
  onSuccess?: () => void
}

export function ClinicCreationForm({ onSuccess }: ClinicCreationFormProps) {
  const { useCreateClinic } = useClinics()
  const createClinic = useCreateClinic()
  const [isSubmitting, setIsSubmitting] = useState(false)

  const {
    register,
    handleSubmit,
    formState: { errors },
    reset,
  } = useForm<ClinicFormValues>({
    resolver: zodResolver(clinicSchema),
    defaultValues: {
      name: '',
      address: '',
      phone: '',
      rfc: '',
    },
  })

  const onSubmit = async (data: ClinicFormValues) => {
    try {
      setIsSubmitting(true)
      await createClinic.mutateAsync(data)
      toast({
        title: 'Clinic created',
        description: 'Your clinic has been successfully created.',
      })
      reset()
      if (onSuccess) {
        onSuccess()
      }
    } catch (error: any) {
      toast({
        title: 'Error',
        description: error.response?.data?.detail || 'Failed to create clinic. Please try again.',
        variant: 'destructive',
      })
    } finally {
      setIsSubmitting(false)
    }
  }

  return (
    <Card className="w-full max-w-md mx-auto">
      <CardHeader>
        <CardTitle>Create Clinic</CardTitle>
        <CardDescription>Register a new clinic in the system.</CardDescription>
      </CardHeader>
      <form onSubmit={handleSubmit(onSubmit)}>
        <CardContent className="space-y-4">
          <div className="space-y-2">
            <Label htmlFor="name">Clinic Name</Label>
            <Input id="name" {...register('name')} placeholder="e.g. MediCare Center" />
            {errors.name && <p className="text-sm text-red-500">{errors.name.message}</p>}
          </div>

          <div className="space-y-2">
            <Label htmlFor="address">Address</Label>
            <Input id="address" {...register('address')} placeholder="Full address" />
            {errors.address && <p className="text-sm text-red-500">{errors.address.message}</p>}
          </div>

          <div className="space-y-2">
            <Label htmlFor="phone">Phone Number</Label>
            <Input id="phone" {...register('phone')} placeholder="Contact phone" />
            {errors.phone && <p className="text-sm text-red-500">{errors.phone.message}</p>}
          </div>

          <div className="space-y-2">
            <Label htmlFor="rfc">RFC (Tax ID)</Label>
            <Input id="rfc" {...register('rfc')} placeholder="Mexican RFC" />
            {errors.rfc && <p className="text-sm text-red-500">{errors.rfc.message}</p>}
          </div>
        </CardContent>
        <CardFooter>
          <Button type="submit" className="w-full" disabled={isSubmitting}>
            {isSubmitting ? 'Creating...' : 'Create Clinic'}
          </Button>
        </CardFooter>
      </form>
    </Card>
  )
}
